import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ChevronLeft } from "lucide-react";
import { useState } from "react";

import { api } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/card";
import { Input, Textarea } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export const Route = createFileRoute("/_authed/prompts/new")({
  component: NewPromptPage,
});

function NewPromptPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [initialTemplate, setInitialTemplate] = useState("");
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: async () => {
      const { data, error: apiError } = await api.POST("/api/v1/admin/prompts", {
        body: {
          code,
          name,
          description: description || null,
          initial_template: initialTemplate,
        },
      });
      if (apiError) {
        const detail = (apiError as { detail?: string })?.detail ?? "create failed";
        throw new Error(String(detail));
      }
      return data!;
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["admin", "prompts"] });
      navigate({ to: "/prompts/$id", params: { id: data.id } });
    },
    onError: (e) => setError(e instanceof Error ? e.message : "create failed"),
  });

  return (
    <div className="space-y-4">
      <Link
        to="/prompts"
        className="inline-flex items-center gap-1 text-sm text-zinc-600 hover:text-zinc-900"
      >
        <ChevronLeft className="size-4" />
        All prompts
      </Link>

      <Card>
        <CardHeader>
          <CardTitle>New prompt</CardTitle>
        </CardHeader>
        <CardBody>
          <form
            className="max-w-xl space-y-5"
            onSubmit={(e) => {
              e.preventDefault();
              setError(null);
              create.mutate();
            }}
          >
            <div className="space-y-2">
              <Label htmlFor="code">Code</Label>
              <Input
                id="code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                required
                placeholder="e.g. report_card.draft_v1"
              />
              <p className="text-xs text-zinc-500">
                Stable internal identifier. Application code calls
                <code className="mx-1 rounded bg-zinc-100 px-1">
                  render_prompt(code=...)
                </code>
                with this. Don't rename after creation.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                placeholder="display name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What does this prompt do? Optional."
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="initial_template">
                Initial template (v1 draft)
              </Label>
              <Textarea
                id="initial_template"
                rows={8}
                value={initialTemplate}
                onChange={(e) => setInitialTemplate(e.target.value)}
                placeholder="Mustache template — leave blank to fill in later."
              />
              <p className="text-xs text-zinc-500">
                v1 starts as a draft. Edit and activate it from the prompt
                detail page.
              </p>
            </div>

            {error && <p className="text-sm text-red-600">{error}</p>}

            <div className="flex gap-2">
              <Button type="submit" disabled={create.isPending}>
                {create.isPending ? "Creating…" : "Create prompt"}
              </Button>
              <Button
                type="button"
                variant="ghost"
                disabled={create.isPending}
                onClick={() => navigate({ to: "/prompts" })}
              >
                Cancel
              </Button>
            </div>
          </form>
        </CardBody>
      </Card>
    </div>
  );
}
